import { useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react'
import { api, isApiError, useNetworkStatus } from '@/api'
import { useActor } from '@/auth/pin/phoneSession'
import { queueBankAccount } from '../../data/actions'
import { Btn, Cta, Footer, Icon, Row, Strip, V1Screen, WaitCta } from '../kit'
import { C } from '../style'
import { tr } from '../text'

/** What OnGrid verification left us: the full number is never returned to the UI. */
export interface CollectedAccount {
  last4: string
  ifsc: string | null
  nameAtBank: string | null
  bankName?: string | null
  branch?: string | null
  city?: string | null
  accountStatus?: string | null
  provider?: string | null
  /** Typed in with no internet: saved on the phone, the server checks it on sync. */
  queued?: boolean
}

type Stage =
  | { kind: 'choose' }
  | { kind: 'number' }
  | { kind: 'done'; account: CollectedAccount }
  | { kind: 'queued'; account: CollectedAccount }
  | { kind: 'failed'; message: string }

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/
function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}

function record(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function isOffline(e: unknown): boolean {
  return isApiError(e) && (e.kind === 'network' || e.kind === 'timeout')
}

function messageOf(e: unknown): string {
  if (isApiError(e)) {
    if (e.kind === 'network' || e.kind === 'timeout') return tr('gate.v1.bank.offline')
    return e.message
  }
  return e instanceof Error ? e.message : String(e)
}

/**
 * Add a farmer's bank account (V1): the passbook number is typed twice, consent is recorded, and
 * OnGrid verifies it. The server encrypts the full number and returns only masked details. The
 * account then waits for a second office person's approval. Never blocks a purchase.
 */
export function BankCollectScreen({
  farmer,
  current,
  header,
  onDone,
  onSkip,
  skipLabel,
}: {
  farmer: { id: string; name: string }
  /** The account he already has, if any: the screen then says a new one replaces it. */
  current?: { state: 'checked' | 'pending' | 'queued'; last4: string } | null
  header: ReactNode
  onDone: (account: CollectedAccount) => void
  onSkip?: () => void
  skipLabel?: string
}) {
  const { online } = useNetworkStatus()
  const actor = useActor()
  const [stage, setStage] = useState<Stage>({ kind: 'choose' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [number, setNumber] = useState('')
  const [again, setAgain] = useState('')
  const [ifsc, setIfsc] = useState('')
  const [name, setName] = useState(farmer.name)
  const [consent, setConsent] = useState(false)

  /** No internet: keep number + IFSC in the sealed outbox; the server runs the check on sync. */
  const queue = async (accountNumber: string) => {
    setBusy(true)
    setError(null)
    try {
      await queueBankAccount(actor.user_id, {
        farmer_id: farmer.id,
        account_number: accountNumber,
        ifsc,
        name_on_passbook: name.trim() || null,
        consent: true,
      })
      setStage({
        kind: 'queued',
        account: { last4: accountNumber.slice(-4), ifsc, nameAtBank: null, queued: true },
      })
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const start = async (accountNumber: string) => {
    setBusy(true)
    setError(null)
    try {
      const r = (await api.post(`/farmers/${farmer.id}/bank-accounts/verify`, {
        accountNumber,
        ifsc,
        nameOnPassbook: name.trim() || undefined,
        consent: true,
      })) as Record<string, unknown>
      const accountId = str(r.accountId)
      if (!accountId) throw new Error('The verification did not return a bank account')
      const verified = record(r.verification)
      // Current servers return the safe OnGrid fields with the verification response. Only ask
      // for the account list as a compatibility fallback for an older server deployment.
      let account: Record<string, unknown> | undefined
      if (Object.keys(verified).length === 0) {
        const list = (await api.get(`/farmers/${farmer.id}/bank-accounts`)) as unknown
        const rows = Array.isArray(list) ? (list as Array<Record<string, unknown>>) : []
        account = rows.find((row) => row.id === accountId)
      }
      setNumber('')
      setAgain('')
      setStage({
        kind: 'done',
        account: {
          last4:
            str(verified.accountLast4) ?? str(account?.accountLast4) ?? accountNumber.slice(-4),
          ifsc: str(verified.ifsc) ?? str(account?.ifsc) ?? ifsc,
          nameAtBank: str(verified.nameAtBank) ?? str(account?.nameAtBank),
          bankName: str(verified.bankName) ?? str(account?.bankName),
          branch: str(verified.branch),
          city: str(verified.city),
          accountStatus: str(verified.accountStatus),
          provider: str(verified.provider) ?? str(account?.provider),
        },
      })
    } catch (e) {
      // The network went while sending the number: keep it on the phone instead.
      if (isOffline(e)) {
        setBusy(false)
        await queue(accountNumber)
        return
      }
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const digits = number.replace(/\D/g, '')
  const numberOk = /^\d{6,18}$/.test(digits)
  const sameTwice = numberOk && again.replace(/\D/g, '') === digits
  const ifscOk = IFSC_RE.test(ifsc)

  const skip = onSkip ? (
    <Btn icon="next" onClick={onSkip}>
      {skipLabel ?? tr('gate.v1.bank.later')}
    </Btn>
  ) : null

  let body: ReactNode
  let footer: ReactNode
  switch (stage.kind) {
    case 'choose':
      body = (
        <>
          <Strip icon="passbook">
            {current
              ? tr(
                  current.state === 'checked'
                    ? 'gate.v1.bank.replaceChecked'
                    : current.state === 'pending'
                      ? 'gate.v1.bank.replacePending'
                      : 'gate.v1.bank.replaceQueued',
                  { name: farmer.name, last4: current.last4 },
                )
              : tr('gate.v1.bank.why', { name: farmer.name })}
          </Strip>
          {!online && <Strip icon="bang">{tr('gate.v1.bank.offlineQueue')}</Strip>}
          <ChoiceTile
            icon="passbook"
            title={tr('gate.v1.bank.number')}
            line={tr(online ? 'gate.v1.bank.numberLine' : 'gate.v1.bank.numberLineOffline')}
            disabled={busy}
            onClick={() => setStage({ kind: 'number' })}
          />
        </>
      )
      footer = skip ? <Footer>{skip}</Footer> : null
      break
    case 'number':
      body = (
        <>
          <Field label={tr('gate.v1.bank.accountNo')}>
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value.replace(/[^\d]/g, '').slice(0, 18))}
              inputMode="numeric"
              autoComplete="off"
              aria-label={tr('gate.v1.bank.accountNo')}
              enterKeyHint="next"
              onFocus={keepInView}
              onKeyDown={nextOnEnter}
              style={inputStyle}
            />
          </Field>
          <Field
            label={tr('gate.v1.bank.accountNoAgain')}
            error={again && !sameTwice ? tr('gate.v1.bank.notSame') : null}
          >
            <input
              value={again}
              onChange={(e) => setAgain(e.target.value.replace(/[^\d]/g, '').slice(0, 18))}
              inputMode="numeric"
              autoComplete="off"
              aria-label={tr('gate.v1.bank.accountNoAgain')}
              enterKeyHint="next"
              onFocus={keepInView}
              onKeyDown={nextOnEnter}
              style={inputStyle}
            />
          </Field>
          <Field
            label={tr('gate.v1.bank.ifsc')}
            error={ifsc.length >= 11 && !ifscOk ? tr('gate.v1.bank.ifscBad') : null}
          >
            <input
              value={ifsc}
              onChange={(e) =>
                setIfsc(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                    .slice(0, 11),
                )
              }
              autoComplete="off"
              autoCapitalize="characters"
              aria-label={tr('gate.v1.bank.ifsc')}
              enterKeyHint="next"
              onFocus={keepInView}
              onKeyDown={nextOnEnter}
              placeholder="SBIN0001234"
              style={inputStyle}
            />
          </Field>
          <Field label={tr('gate.v1.bank.nameOnPassbook')}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              autoComplete="off"
              aria-label={tr('gate.v1.bank.nameOnPassbook')}
              enterKeyHint="done"
              onFocus={keepInView}
              onKeyDown={nextOnEnter}
              style={inputStyle}
            />
          </Field>
          <Strip icon="lock">{tr('gate.v1.bank.numberNotKept')}</Strip>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 16,
              lineHeight: 1.35,
            }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ width: 22, height: 22, marginTop: 1 }}
            />
            <span>{tr('gate.v1.bank.consent')}</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
            <Row>
              <Btn icon="back" onClick={() => setStage({ kind: 'choose' })}>
                {tr('gate.v1.back')}
              </Btn>
            </Row>
            {sameTwice && ifscOk && consent && !online ? (
              <Cta
                label={tr('gate.v1.bank.saveQueued')}
                icon="ok"
                busy={busy}
                onClick={() => void queue(digits)}
              />
            ) : sameTwice && ifscOk && consent ? (
              <Cta
                label={tr('gate.v1.bank.verify')}
                icon="ok"
                busy={busy}
                onClick={() => void start(digits)}
              />
            ) : (
              <WaitCta>{tr('gate.v1.bank.fillAll')}</WaitCta>
            )}
          </div>
        </>
      )
      // The keyboard takes half the screen: a pinned footer would leave a sliver to scroll in, so
      // the actions scroll with the form here.
      footer = null
      break
    case 'done':
      body = (
        <>
          <div
            style={{
              borderRadius: 12,
              border: `2px solid ${C.ok}`,
              background: C.okWash,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <b style={{ fontSize: 20 }}>
              {tr('gate.v1.bank.doneAccount', { last4: stage.account.last4 })}
            </b>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: C.ok }}>
              <Icon name="ok" size={18} />
              <b style={{ fontSize: 16 }}>
                {tr('gate.v1.bank.onGridResult', {
                  status: stage.account.accountStatus ?? 'ACTIVE',
                })}
              </b>
            </span>
            {stage.account.nameAtBank && (
              <span style={{ fontSize: 17 }}>
                {tr('gate.v1.bank.nameAtBank', { name: stage.account.nameAtBank })}
              </span>
            )}
            {!stage.account.nameAtBank && (
              <span style={{ fontSize: 16, color: C.bad, fontWeight: 700 }}>
                {tr('gate.v1.bank.nameNotReturned')}
              </span>
            )}
            {stage.account.bankName && (
              <span style={{ fontSize: 16 }}>
                {tr('gate.v1.bank.bankName', { bank: stage.account.bankName })}
              </span>
            )}
            {stage.account.ifsc && (
              <span style={{ fontSize: 15, color: C.muted }}>IFSC {stage.account.ifsc}</span>
            )}
            {(stage.account.branch || stage.account.city) && (
              <span style={{ fontSize: 15, color: C.muted }}>
                {[stage.account.branch, stage.account.city].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <Strip icon="showfarmer">{tr('gate.v1.bank.confirmResult')}</Strip>
          <Strip icon="supervisor">{tr('gate.v1.bank.officeApproves')}</Strip>
        </>
      )
      footer = (
        <Footer>
          <Cta label={tr('gate.v1.next')} icon="next" onClick={() => onDone(stage.account)} />
        </Footer>
      )
      break
    case 'queued':
      body = (
        <>
          <div
            style={{
              borderRadius: 12,
              border: `2px solid ${C.accDark}`,
              background: C.surface,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <b style={{ fontSize: 20 }}>
              {tr('gate.v1.bank.queuedAccount', { last4: stage.account.last4 })}
            </b>
            {stage.account.ifsc && (
              <span style={{ fontSize: 15, color: C.muted }}>IFSC {stage.account.ifsc}</span>
            )}
          </div>
          <Strip icon="clock">{tr('gate.v1.bank.queuedLine')}</Strip>
        </>
      )
      footer = (
        <Footer>
          <Cta label={tr('gate.v1.next')} icon="next" onClick={() => onDone(stage.account)} />
        </Footer>
      )
      break
    case 'failed':
      body = <Strip icon="bang">{stage.message}</Strip>
      footer = (
        <Footer>
          <Row>{skip}</Row>
          <Cta
            label={tr('gate.v1.bank.tryAgain')}
            icon="undo"
            onClick={() => setStage({ kind: 'choose' })}
          />
        </Footer>
      )
      break
  }

  return (
    <V1Screen main={{ gap: 10 }} header={header} footer={footer}>
      {body}
      {error && <Strip icon="bang">{error}</Strip>}
    </V1Screen>
  )
}

/** After the keyboard opens and the screen shrinks, bring the field being typed into view. */
function keepInView(e: FocusEvent<HTMLInputElement>) {
  const el = e.currentTarget
  window.setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
}

/** Enter on the keyboard moves to the next field, so nobody has to scroll to find it. */
function nextOnEnter(e: KeyboardEvent<HTMLInputElement>) {
  if (e.key !== 'Enter') return
  e.preventDefault()
  const inputs = [
    ...(e.currentTarget
      .closest('main')
      ?.querySelectorAll<HTMLInputElement>('input:not([type=checkbox])') ?? []),
  ]
  const next = inputs[inputs.indexOf(e.currentTarget) + 1]
  if (next) next.focus()
  else e.currentTarget.blur()
}

const inputStyle = {
  height: 54,
  width: '100%',
  padding: '0 12px',
  borderRadius: 12,
  border: `2px solid ${C.acc}`,
  background: C.surface,
  fontFamily: "'Mukta', sans-serif",
  fontSize: 22,
  fontWeight: 600,
  color: C.ink,
  letterSpacing: '.03em',
  outline: 'none',
  boxSizing: 'border-box' as const,
}

function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string | null
  children: ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 16, fontWeight: 600, color: C.muted }}>{label}</span>
      {children}
      {error && <span style={{ fontSize: 15, fontWeight: 700, color: C.bad }}>{error}</span>}
    </label>
  )
}

function ChoiceTile({
  icon,
  title,
  line,
  disabled,
  onClick,
}: {
  icon: 'phone' | 'passbook'
  title: string
  line: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 84,
        padding: '10px 14px',
        borderRadius: 14,
        border: `2px solid ${C.ink}`,
        background: C.surface,
        color: C.ink,
        textAlign: 'left',
        fontFamily: "'Mukta', sans-serif",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <Icon name={icon} size={34} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <b style={{ fontSize: 20, lineHeight: 1.2 }}>{title}</b>
        <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>{line}</span>
      </span>
    </button>
  )
}
